"use client"

/**
 * AdTriggerOverlay — a floating, admin-only "fire this ad right now" panel
 * that sits on top of whatever page you're actually browsing. Different
 * from the existing full-page harness (components/ads/AdTestPanel.tsx,
 * mounted at /ads/preview): that one is a dedicated page you navigate to;
 * this one floats over real content so you can see how a placement looks
 * and behaves layered over an actual page, and reach it without leaving
 * wherever you're testing.
 *
 * Covers everything AdTestPanel doesn't: the sitewide corner widgets
 * (Sticky Banner, Video Slider, In-Page Push, Instant Message — triggered
 * via lib/ad-trigger-store.ts, since those components are mounted once in
 * app/layout.tsx and normally only fetch on mount/cooldown-expiry), both
 * Popunders, Push Notifications, and the three placements that render
 * inline (In-Video Pause Banner, Outstream Video, Recommendation Widget —
 * shown directly in the panel instead of needing a real video/feed
 * context). Interstitial/Preroll/Inline-grid already have a working
 * harness at /ads/preview, linked at the bottom rather than duplicated.
 *
 * Positioned bottom-center — the one screen edge not already claimed by a
 * corner-docked ad placement (top-left/top-right/bottom-left/bottom-right
 * are Sticky Banner/In-Page Push/Video Slider/Instant Message respectively).
 */

import { useEffect, useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Zap, ExternalLink } from "@/lib/icons"
import { useAuthStore } from "@/lib/auth-store"
import { useAdTriggerStore } from "@/lib/ad-trigger-store"
import { useAdStore } from "@/lib/ad-store"
import { PauseOverlayAd } from "@/components/ads/PauseOverlayAd"
import { RecommendationWidgetAd } from "@/components/ads/RecommendationWidgetAd"
import { InFeedVideoAd, type InFeedVideoAdData } from "@/components/ads/InFeedVideoAd"
import { useTranslations } from "next-intl"

// super_admin only (was admin+super_admin) — tightened 2026-09-08 alongside
// making this dormant by default (see DORMANT below). This floating button
// sits right next to the real Skip-Ads button on every page for every
// visitor to see; now that ad testing for this round of placements is done,
// there's no reason a plain "admin" (as opposed to super_admin) needs it
// live, and no reason it should be showing to anyone at all until testing
// resumes.
const ADMIN_ROLES = ["super_admin"]
// Flip to false to bring this back for testing. Kept as an explicit,
// obvious switch rather than deleting the component — the panel itself
// (all its trigger wiring) is unchanged and still works, it's just not
// mounted-visible by default anymore.
const DORMANT = true

export function AdTriggerOverlay() {
  const tt = useTranslations("adTriggerOverlay")
  const { profile } = useAuthStore()
  const [open, setOpen] = useState(false)

  // "admin"/"super_admin" is always treated as premium/ad-free (see
  // hasPremiumAccess() sync in lib/auth-store.ts) — same fact AdTestPanel.tsx's own
  // override comment documents, and the same fix, just scoped to the
  // dialog being open instead of a whole dedicated page. Every placement
  // gated on raw `isPremiumUser` (every sitewide widget, PauseOverlayAd)
  // silently produces nothing for the only accounts that can ever open
  // this panel — confirmed live via a debug log: PauseOverlayAd's fetch
  // effect bailed with `isPremiumUser: true` and never even made a
  // network request, no matter how many times "Show In-Video Pause
  // Banner" was clicked. A one-time force isn't enough: auth-store
  // re-syncs isPremiumUser from the profile on every SIGNED_IN/
  // INITIAL_SESSION event, re-asserting true and racing this override —
  // same reasoning as AdTestPanel's own comment — so this subscribes for
  // as long as the dialog stays open and restores the real value the
  // moment it closes, rather than permanently stripping this admin's
  // normal ad-free browsing whenever they're not actively testing.
  useEffect(() => {
    if (!open) return
    const wasPremium = useAdStore.getState().isPremiumUser
    useAdStore.getState().setIsPremiumUser(false)
    const unsubscribe = useAdStore.subscribe((state) => {
      if (state.isPremiumUser) useAdStore.getState().setIsPremiumUser(false)
    })
    return () => {
      unsubscribe()
      useAdStore.getState().setIsPremiumUser(wasPremium)
    }
  }, [open])

  if (DORMANT) return null
  if (!profile?.role || !ADMIN_ROLES.includes(profile.role)) return null

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 h-10 w-10 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
        aria-label={tt("openAdTriggerPanel")}
        title={tt("triggerAnAdOnDemand")}
      >
        <Zap className="h-4 w-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-4 w-4" /> {tt("adTriggerPanel")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            {tt("firesEachPlacementOverWhateverPageIsOpen")}
          </p>

          <CornerWidgetSection />
          <PopunderTriggerSection />
          <PushNotificationTriggerSection />
          <InlinePreviewSection />

          <div className="pt-2 border-t">
            <Link href="/ads/preview" target="_blank">
              <Button variant="outline" size="sm" className="w-full">
                <ExternalLink className="h-3.5 w-3.5 mr-2" />
                {tt("openFullHarnessInterstitialPrerollInli")}
              </Button>
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2 py-2 border-t first:border-t-0 first:pt-0">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{title}</p>
      {children}
    </div>
  )
}

function CornerWidgetSection() {
  const tt = useTranslations("adTriggerOverlay")
  const { triggerStickyBanner, triggerVideoSlider, triggerInPagePush, triggerInstantMessage } = useAdTriggerStore()
  return (
    <Section title={tt("sitewideCornerWidgets")}>
      <div className="grid grid-cols-2 gap-2">
        <Button size="sm" variant="secondary" onClick={triggerStickyBanner}>{tt("stickyBannerTopLeft")}</Button>
        <Button size="sm" variant="secondary" onClick={triggerVideoSlider}>{tt("videoSliderBottomRight")}</Button>
        <Button size="sm" variant="secondary" onClick={triggerInPagePush}>{tt("inPagePushTopRight")}</Button>
        <Button size="sm" variant="secondary" onClick={triggerInstantMessage}>{tt("instantMessageBottomLeft")}</Button>
      </div>
    </Section>
  )
}

function PopunderTriggerSection() {
  const tt = useTranslations("adTriggerOverlay")
  const [status, setStatus] = useState<Record<"desktop" | "mobile", string>>({ desktop: "", mobile: "" })

  const trigger = async (variant: "desktop" | "mobile") => {
    setStatus((s) => ({ ...s, [variant]: "loading" }))
    try {
      const res = await fetch("/api/admin/popunder-config")
      const config = await res.json()
      if (!config.enabled) {
        setStatus((s) => ({ ...s, [variant]: "Popunder is disabled (see admin_settings)." }))
        return
      }
      const tier = config.max_tier ?? 3
      const key = (t: 1 | 2 | 3) => (variant === "mobile" ? `embed_code_mobile_tier${t}` : `embed_code_tier${t}`)
      const embedCode: string =
        tier === 1 ? (config[key(1)] || config[key(2)] || config[key(3)]) :
        tier === 2 ? (config[key(2)] || config[key(1)] || config[key(3)]) :
                     (config[key(3)] || config[key(2)] || config[key(1)])
      if (!embedCode) {
        setStatus((s) => ({ ...s, [variant]: "No zone configured for this tier." }))
        return
      }
      if (document.querySelector(`iframe[data-popunder-test="${variant}"]`)) {
        setStatus((s) => ({ ...s, [variant]: "Already armed — click anywhere on the page." }))
        return
      }
      // Load the iframe INSIDE the click handler, not before it. This used
      // to create the (hidden, pointer-events:none, 1x1) iframe immediately
      // and just tell the user to click — but the popunder script that then
      // loaded into it has no way to ever see that click: it can only
      // listen for interaction within its own tiny invisible document, and
      // the user is clicking the real page, not that box. Reported live as
      // "click to test them, it just shows nothing." The real
      // PopunderInjector (components/ads/popunder-injector.tsx) has always
      // done this correctly — it attaches the click listener to `document`
      // (the parent page) FIRST and only creates the iframe once a real
      // click has actually happened. This mirrors that exactly.
      const armPopunder = () => {
        if (document.querySelector(`iframe[data-popunder-test="${variant}"]`)) return
        const srcDoc = `<!DOCTYPE html><html><head></head><body>${embedCode}</body></html>`
        const frame = document.createElement("iframe")
        frame.setAttribute("data-popunder-test", variant)
        frame.setAttribute("sandbox", "allow-scripts allow-popups allow-popups-to-escape-sandbox")
        frame.style.cssText = "position:fixed;width:1px;height:1px;bottom:0;left:0;opacity:0;border:0;pointer-events:none;z-index:-1"
        frame.srcdoc = srcDoc
        document.body.appendChild(frame)
        // Same bounded one-shot focus-reclaim as the real injector
        // (components/ads/popunder-injector.tsx) — see that file's comment
        // for why a tab-stealing new window can't be prevented outright,
        // only reclaimed from afterward.
        const reclaim = () => { window.removeEventListener("blur", reclaim); window.focus() }
        window.addEventListener("blur", reclaim)
        setTimeout(() => window.removeEventListener("blur", reclaim), 5000)
        setStatus((s) => ({ ...s, [variant]: "Fired — check for a new tab (should return focus here within a second)." }))
      }
      document.addEventListener("click", armPopunder, { capture: true, once: true })
      setStatus((s) => ({ ...s, [variant]: "Armed — click anywhere on the page to fire it." }))
    } catch {
      setStatus((s) => ({ ...s, [variant]: "Failed to load config." }))
    }
  }

  return (
    <Section title={tt("popunder")}>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Button size="sm" variant="secondary" className="w-full" onClick={() => trigger("desktop")}>
            {tt("armDesktopPopunder")}
          </Button>
          {status.desktop && <p className="text-[11px] text-muted-foreground">{status.desktop}</p>}
        </div>
        <div className="space-y-1">
          <Button size="sm" variant="secondary" className="w-full" onClick={() => trigger("mobile")}>
            {tt("armMobilePopunder")}
          </Button>
          {status.mobile && <p className="text-[11px] text-muted-foreground">{status.mobile}</p>}
        </div>
      </div>
    </Section>
  )
}

function PushNotificationTriggerSection() {
  const tt = useTranslations("adTriggerOverlay")
  const [status, setStatus] = useState("")

  const trigger = async () => {
    setStatus("loading")
    try {
      const res = await fetch("/api/ads/network-grid-ad?type=push_notifications&tier=3&preview=1")
      const d = res.ok ? await res.json() : null
      if (!d?.display_js) {
        setStatus("No zone configured or zone is inactive.")
        return
      }
      if (document.querySelector("[data-push-notif-test]")) {
        setStatus("Already injected this session.")
        return
      }
      const marker = document.createElement("meta")
      marker.setAttribute("data-push-notif-test", "1")
      document.head.appendChild(marker)
      const doc = new DOMParser().parseFromString(d.display_js, "text/html")
      doc.querySelectorAll("script").forEach((old) => {
        const s = document.createElement("script")
        s.type = old.type || "text/javascript"
        if (old.src) s.src = old.src
        else s.textContent = old.textContent
        document.body.appendChild(s)
      })
      setStatus("Injected — the real browser permission prompt should appear now.")
    } catch {
      setStatus("Failed to load config.")
    }
  }

  return (
    <Section title={tt("pushNotifications")}>
      <Button size="sm" variant="secondary" onClick={trigger}>{tt("triggerPermissionPrompt")}</Button>
      {status && <p className="text-[11px] text-muted-foreground mt-1">{status}</p>}
      <p className="text-[11px] text-muted-foreground">
        {tt("bypassesTheOneTimeAlreadyAskedFlagThis")}
      </p>
    </Section>
  )
}

function InlinePreviewSection() {
  const tt = useTranslations("adTriggerOverlay")
  const [showPause, setShowPause] = useState(false)
  const [outstreamAd, setOutstreamAd] = useState<InFeedVideoAdData | null>(null)
  const [outstreamStatus, setOutstreamStatus] = useState("")
  const [showRecommendation, setShowRecommendation] = useState(false)

  const triggerOutstream = async () => {
    setOutstreamStatus("loading")
    try {
      const res = await fetch("/api/ads/network-grid-ad?type=outstream_video&tier=3&preview=1")
      const d = res.ok ? await res.json() : null
      if (!d?.display_js) {
        setOutstreamStatus("No zone configured or zone is inactive.")
        return
      }
      setOutstreamAd({ id: `trigger-${d.zone_id}`, campaign_id: d.zone_id ?? "outstream_video", embed_js: d.display_js, badge_text: "Ad" })
      setOutstreamStatus("")
    } catch {
      setOutstreamStatus("Failed to load config.")
    }
  }

  return (
    <Section title={tt("inlinePlacementsPreviewedRightHere")}>
      <div className="space-y-3">
        <div>
          <Button size="sm" variant="secondary" onClick={() => setShowPause((v) => !v)}>
            {tt("hideShowInVideoPauseBanner", { action: showPause ? tt("hide") : tt("show") })}
          </Button>
          {showPause && (
            <div className="relative h-[280px] mt-2 rounded-lg bg-black/80 border">
              <PauseOverlayAd onDismiss={() => setShowPause(false)} />
            </div>
          )}
        </div>
        <div>
          <Button size="sm" variant="secondary" onClick={triggerOutstream}>{tt("showOutstreamVideo")}</Button>
          {outstreamStatus && <p className="text-[11px] text-muted-foreground mt-1">{outstreamStatus}</p>}
          {outstreamAd && (
            <div className="mt-2 max-w-xs">
              <InFeedVideoAd ad={outstreamAd} className="aspect-video" />
            </div>
          )}
        </div>
        <div>
          <Button size="sm" variant="secondary" onClick={() => setShowRecommendation((v) => !v)}>
            {tt("hideShowRecommendationWidget", { action: showRecommendation ? tt("hide") : tt("show") })}
          </Button>
          {showRecommendation && (
            <div className="mt-2">
              <RecommendationWidgetAd />
            </div>
          )}
        </div>
      </div>
    </Section>
  )
}
